// Who is signed in.
//
// Retool handled the session and only ever rendered the app to an authenticated
// user, so App.tsx assumes a user exists. This keeps that assumption true: the
// app tree is only mounted once /api/auth/me has confirmed a session.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

export interface CurrentUser {
  id: string
  email: string
  fullName: string
}

interface AuthState {
  user: CurrentUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch('/api/auth/me', { credentials: 'same-origin' })
        if (!cancelled && response.ok) {
          const body = (await response.json()) as { user: CurrentUser }
          setUser(body.user)
        }
      } catch {
        // Offline or the server is down; treated the same as signed out.
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ email, password }),
    })
    const body = (await response.json().catch(() => ({}))) as {
      user?: CurrentUser
      error?: string
    }
    if (!response.ok || !body.user) {
      throw new Error(body.error ?? 'Could not sign in')
    }
    // The login response can succeed even when the browser rejects Set-Cookie
    // (for example, a Secure production cookie on an HTTP Coolify URL).
    // Confirm the cookie round-trip before mounting the app or navigating.
    const session = await fetch('/api/auth/me', {
      credentials: 'same-origin',
      cache: 'no-store',
    })
    if (session.status === 401) {
      throw new Error(
        window.location.protocol === 'http:'
          ? 'Your browser did not retain the session. Open the app using its HTTPS address and sign in again.'
          : 'Your browser did not retain the session. Allow cookies for this site and try again.',
      )
    }
    if (!session.ok) throw new Error('Could not verify your session. Please try again.')
    const confirmed = (await session.json()) as { user: CurrentUser }
    setUser(confirmed.user)
  }, [])

  const signOut = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
    setUser(null)
    // Clear any cached query data held in module scope (hooks/cachedQueries.ts).
    window.location.assign('/login')
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>{children}</AuthContext.Provider>
  )
}
